/**
 * Portal slugs — the first path segment of `https://<hostname>/<slug>`.
 *
 * The slug shares its namespace with the app's own routes on that hostname, so
 * anything that could shadow a framework or asset path is reserved. Client-safe
 * (pure string work) and re-exported from `@repo/core/client` so the settings
 * form validates against exactly the rules the server enforces.
 */

import { stripWwwFromDomain } from "../../utils/domain";
import { getPropertySlug } from "../../utils/property-label";

/**
 * Lowercase, dot/hyphen-separated, 1–63 characters, starting and ending
 * alphanumeric. Dots are allowed so a slug can just be the brand's domain.
 */
export const PORTAL_SLUG_RE = /^[a-z0-9](?:[a-z0-9.-]{0,61}[a-z0-9])?$/;

/** Longest slug `PORTAL_SLUG_RE` will accept. */
const PORTAL_SLUG_MAX = 63;

/**
 * Path segments the portal host serves itself. A site slug matching one of
 * these would be unreachable at best and would shadow a real route at worst.
 */
export const PORTAL_RESERVED_SLUGS: ReadonlySet<string> = new Set([
	"api",
	"_next",
	"unlock",
	"portal",
	"images",
	"fonts",
	"image-proxy",
	"favicon.ico",
	// The portal's own favicon route, served at https://<hostname>/icon.
	"icon",
	"robots.txt",
	"sitemap.xml",
	"design",
	"app",
	"auth",
	"docs",
	".well-known",
	"monitoring-tunnel",
]);

/** Trim and lowercase. Does not validate — pair with {@link validatePortalSlug}. */
export function normalizePortalSlug(input: string): string {
	return input.trim().toLowerCase();
}

/**
 * Reduce anything domain-shaped to a bare lowercase host: drop the scheme,
 * path, query and port a pasted URL brings with it.
 *
 * Has to run *before* `stripWwwFromDomain`, which only matches a leading
 * "www." and would leave it in place on "https://www.acme.com".
 */
function bareHost(raw: string): string {
	let host = raw.trim().toLowerCase();
	if (!host) {
		return "";
	}
	host = host.replace(/^[a-z][a-z0-9+.-]*:\/\//, "");
	host = host.split(/[/?#]/)[0] ?? "";
	return host.split(":")[0] ?? "";
}

/**
 * Coerce arbitrary text into something `PORTAL_SLUG_RE` accepts, or "" when
 * nothing usable survives.
 */
function sanitizeSlug(raw: string): string {
	let slug = bareHost(raw);
	if (!slug) {
		return "";
	}

	slug = slug.replace(/[^a-z0-9.-]+/g, "-");
	// Strip leading/trailing punctuation the regex forbids at the edges.
	slug = slug.replace(/^[^a-z0-9]+/, "").replace(/[^a-z0-9]+$/, "");

	if (slug.length > PORTAL_SLUG_MAX) {
		slug = slug.slice(0, PORTAL_SLUG_MAX).replace(/[^a-z0-9]+$/, "");
	}

	return PORTAL_SLUG_RE.test(slug) ? slug : "";
}

/**
 * The slug a property gets when the agency does not pick one.
 *
 * Prefers the brand's own domain — that is what a client recognises — then a
 * slugified company name (PERSON and PRODUCT subjects have no domain), then a
 * generic fallback. Always satisfies {@link PORTAL_SLUG_RE}; uniqueness is the
 * caller's problem.
 *
 * @example
 * defaultPortalSlug({ domain: "www.Acme.com" })          // "acme.com"
 * defaultPortalSlug({ companyName: "Jane Roe" })         // "jane-roe"
 * defaultPortalSlug({})                                  // "client"
 */
export function defaultPortalSlug(property: {
	domain?: string | null;
	companyName?: string | null;
}): string {
	const domain = property.domain?.trim();
	if (domain) {
		const fromDomain = sanitizeSlug(stripWwwFromDomain(bareHost(domain)));
		if (fromDomain) {
			return fromDomain;
		}
	}

	const companyName = property.companyName?.trim();
	if (companyName) {
		// getPropertySlug is filesystem-oriented (lowercase, non-alphanumerics to
		// "-"), which is a subset of what PORTAL_SLUG_RE allows — but it does not
		// bound length, so it still goes through sanitizeSlug.
		const fromName = sanitizeSlug(
			getPropertySlug({ companyName, domain: null }, ""),
		);
		if (fromName) {
			return fromName;
		}
	}

	return "client";
}

export type PortalSlugValidation =
	| { ok: true }
	| { ok: false; reason: "invalid" | "reserved" };

/**
 * Check a normalized slug.
 *
 * Reserved names are checked before the format, because several of them
 * ("favicon.ico", ".well-known") do not satisfy `PORTAL_SLUG_RE` and reporting
 * them as merely "invalid" would hide the real reason from the agency.
 */
export function validatePortalSlug(slug: string): PortalSlugValidation {
	if (PORTAL_RESERVED_SLUGS.has(slug)) {
		return { ok: false, reason: "reserved" };
	}
	if (!PORTAL_SLUG_RE.test(slug)) {
		return { ok: false, reason: "invalid" };
	}
	return { ok: true };
}
